import { create } from 'zustand';

interface AppError {
  id: string;
  title: string;
  message: string;
  type: 'error' | 'warning' | 'info';
  duration?: number | null;
  timestamp: number;
}

interface ErrorStore {
  // State
  errors: AppError[];
  currentError: AppError | null;
  isToastOpen: boolean;

  // Actions
  addError: (error: Omit<AppError, 'id' | 'timestamp'>) => void;
  removeError: (id: string) => void;
  showNext: () => void;
  dismiss: () => void;
  clear: () => void;
}

const generateId = () => Math.random().toString(36).substring(2, 15);

const DURATIONS = {
  error: null,
  warning: 4000,
  info: 3000,
} as const;

export const useErrorStore = create<ErrorStore>()((set, get) => ({
  // Initial state
  errors: [],
  currentError: null,
  isToastOpen: false,

  // Add error to queue
  addError: (errorData) => {
    const error: AppError = {
      ...errorData,
      id: generateId(),
      timestamp: Date.now(),
      duration: errorData.duration ?? DURATIONS[errorData.type],
    };

    set((state) => ({ errors: [...state.errors, error] }));

    // Show immediately if no toast is open
    if (!get().isToastOpen) {
      get().showNext();
    }
  },

  // Remove specific error
  removeError: (id) => {
    set((state) => ({
      errors: state.errors.filter((error) => error.id !== id),
    }));
  },

  // Show next error in queue
  showNext: () => {
    const { errors, isToastOpen } = get();

    if (isToastOpen || errors.length === 0) {
      return;
    }

    const nextError = errors[0];
    set({
      currentError: nextError,
      isToastOpen: true,
    });
  },

  // Dismiss current error
  dismiss: () => {
    const { currentError } = get();

    if (!currentError) {
      // If no current error but toast is somehow open, close it
      set({ isToastOpen: false });
      return;
    }

    set((state) => ({
      errors: state.errors.filter((error) => error.id !== currentError.id),
      currentError: null,
      isToastOpen: false,
    }));

    // Show next error after delay
    setTimeout(() => {
      const { errors, isToastOpen } = get();
      if (errors.length > 0 && !isToastOpen) {
        get().showNext();
      }
    }, 300);
  },

  // Clear all errors
  clear: () => {
    set({
      errors: [],
      currentError: null,
      isToastOpen: false,
    });
  },
}));

// Convenience functions
export const showError = (message: string, title = 'Error') => {
  useErrorStore.getState().addError({
    title,
    message,
    type: 'error',
  });
};
